import * as React from 'react';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
// @mui
import {
  Box,
  Modal,
  Button,
  Stack,
  TextField,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  MenuItem,
  Typography,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ptBR from 'date-fns/locale/pt-BR';

import { getUsuarios, getGenericData } from './api';
import { cores, labelCampoEscuro } from '../styles/styleUtils';
import { TIPOS_ATIVIDADE_SELECIONAVEIS } from './tiposAtividade';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '50vw',
  maxHeight: '90vh',
  '@media screen and (max-width: 600px)': {
    width: '92vw',
  },
  bgcolor: 'transparent',
  border: `1px solid ${cores.chumbo}`,
  borderTop: `4px solid ${cores.laranja}`,
  boxShadow: 24,
  p: 3,
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  overflowY: 'auto',
  background: `linear-gradient(180deg, ${cores.painelTopo} 0%, ${cores.painelBase} 100%)`,
};

const sfWhite = { backgroundColor: 'white', ...labelCampoEscuro, width: '100%' };

// eslint-disable-next-line no-unused-vars
const ModalAtividadade = React.forwardRef((props, ref) => {
  const usuarioLogado = JSON.parse(localStorage.getItem('userData'));

  const [atividade, setAtividade] = useState({
    data: new Date(),
    usuario: usuarioLogado,
    tipo: '',
    descricao: '',
    proximaAcao: false,
  });

  const [usuarios, setUsuarios] = useState([]);
  const [tipos, setTipos] = useState(TIPOS_ATIVIDADE_SELECIONAVEIS);

  useEffect(() => {
    const fetchUsuarios = async () => {
      try {
        const u = await getUsuarios();
        setUsuarios(u || []);
      } catch (e) {
        console.error('Erro ao buscar usuários:', e);
      }
    };
    fetchUsuarios();

    // Segue o mesmo padrão de MotivoAgenda (Agenda.js): se existir um seed
    // cadastrado no banco (coleção 'tipoatividade'), usa os nomes de lá.
    // Se a coleção ainda não existir ou vier vazia, mantém a lista fixa
    // importada de tiposAtividade.js — não quebra o formulário.
    const fetchTipos = async () => {
      try {
        const t = await getGenericData('tipoatividade', {});
        if (t && t.length > 0) setTipos(t.map((item) => item.nome));
      } catch (e) {
        // seed ainda não existe no backend — segue com a lista padrão
      }
    };
    fetchTipos();
  }, []);

  const podeSalvar = !!atividade.tipo && !!atividade.data;

  const handleSalvar = () => {
    if (!podeSalvar) return;
    props.onConfirm({ ...atividade });
  };

  return (
    <Modal
      open={props.open}
      onClose={props.onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style}>
        <Typography variant="subtitle1" sx={{ color: cores.laranja, fontWeight: 'bold' }}>
          Nova atividade
        </Typography>

        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
          <DatePicker
            label="Data"
            value={atividade.data}
            onChange={(newValue) => setAtividade((prev) => ({ ...prev, data: newValue }))}
            renderInput={(params) => <TextField {...params} fullWidth sx={sfWhite} />}
          />
        </LocalizationProvider>

        <Autocomplete
          disablePortal
          options={usuarios}
          value={atividade.usuario}
          isOptionEqualToValue={(option, value) => option._id === value._id}
          getOptionLabel={(option) => option?.nome || ''}
          onChange={(_, value) => setAtividade((prev) => ({ ...prev, usuario: value }))}
          renderInput={(params) => <TextField {...params} label="Usuário responsável" sx={sfWhite} />}
        />

        <TextField
          select
          label="Tipo de atividade"
          value={atividade.tipo}
          onChange={(e) => setAtividade((prev) => ({ ...prev, tipo: e.target.value }))}
          sx={sfWhite}
        >
          {tipos.map((t) => (
            <MenuItem key={t} value={t}>
              {t}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          multiline
          rows={4}
          label="Descrição / Observação"
          value={atividade.descricao}
          onChange={(e) => setAtividade((prev) => ({ ...prev, descricao: e.target.value }))}
          sx={sfWhite}
        />

        <FormControlLabel
          sx={{ color: cores.textoClaro, alignSelf: 'flex-start' }}
          control={
            <Checkbox
              checked={atividade.proximaAcao}
              onChange={(e) => setAtividade((prev) => ({ ...prev, proximaAcao: e.target.checked }))}
              sx={{
                color: cores.textoClaro,
                '&.Mui-checked': { color: cores.laranja },
              }}
            />
          }
          label="Definir como próxima ação"
        />

        <Stack direction="row" justifyContent="space-between" gap={2} mt={1}>
          <Button variant="outlined" onClick={props.onClose} color="error">
            Cancelar
          </Button>
          <Button variant="contained" color="success" disabled={!podeSalvar} onClick={handleSalvar}>
            Salvar atividade
          </Button>
        </Stack>
      </Box>
    </Modal>
  );
});

ModalAtividadade.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
};

export default ModalAtividadade;